/**
 * Journal Entries List - View and search posted journal entries
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { 
  Book, 
  Search, 
  Loader2, 
  RefreshCw, 
  ChevronLeft, 
  ChevronRight,
  Eye,
  FileText,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Format currency
const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value || 0);
};

// Status badge colors
const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  posted: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  reversed: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
};

// Source type labels
const SOURCE_LABELS = {
  invoice: 'Invoice',
  payment: 'Payment',
  manual: 'Manual Entry',
  reversal: 'Reversal',
  commission: 'Commission'
};

// Journal Entry Detail Dialog
const JournalEntryDetail = ({ entry, open, onClose }) => {
  if (!entry) return null;

  const totalDebits = entry.lines?.reduce((sum, line) => sum + (line.debit || 0), 0) || 0;
  const totalCredits = entry.lines?.reduce((sum, line) => sum + (line.credit || 0), 0) || 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {entry.entry_number}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Header Info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-muted-foreground">Status</p>
              <Badge className={STATUS_COLORS[entry.status]}>
                {entry.status?.toUpperCase()}
              </Badge>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Entry Date</p>
              <p className="font-medium">{entry.entry_date}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Source</p>
              <p className="font-medium">{SOURCE_LABELS[entry.source_type] || entry.source_type}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Posted</p>
              <p className="font-medium">
                {entry.posted_at ? format(new Date(entry.posted_at), 'MMM d, yyyy h:mm a') : '—'}
              </p>
            </div>
          </div>

          {/* Memo */}
          {entry.memo && (
            <div>
              <p className="text-xs text-muted-foreground">Memo</p>
              <p className="text-sm mt-1 p-2 bg-slate-50 dark:bg-slate-800 rounded">{entry.memo}</p>
            </div>
          )}

          {/* Lines */}
          <div>
            <h4 className="font-medium mb-2">Journal Lines</h4>
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Account</TableHead>
                      <TableHead className="text-right">Debit</TableHead>
                      <TableHead className="text-right">Credit</TableHead>
                      <TableHead>Description</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entry.lines?.map((line, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <span className="font-mono text-sm text-muted-foreground mr-2">
                            {line.account_code}
                          </span>
                          {line.account_name}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {line.debit > 0 ? formatCurrency(line.debit) : '—'}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {line.credit > 0 ? formatCurrency(line.credit) : '—'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {line.description || '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Totals */}
                    <TableRow className="bg-slate-50 dark:bg-slate-800 font-semibold">
                      <TableCell>TOTALS</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(totalDebits)}</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(totalCredits)}</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>

          {/* Metadata */}
          <div className="text-xs text-muted-foreground">
            <p>Created: {entry.created_at ? format(new Date(entry.created_at), 'MMM d, yyyy h:mm a') : '—'}</p>
            {entry.source_id && <p>Source ID: {entry.source_id}</p>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function JournalEntriesList({ user }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(20);
  
  // Filters
  const [filters, setFilters] = useState({
    status: '',
    source_type: '',
    search: '',
    start_date: '',
    end_date: ''
  });
  
  // Detail dialog
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('skip', page * pageSize);
      params.append('limit', pageSize);
      
      if (filters.status) params.append('status', filters.status);
      if (filters.source_type) params.append('source_type', filters.source_type);
      if (filters.search) params.append('search', filters.search);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);

      const response = await axios.get(`${API}/finance/hub/journal-entries?${params}`);
      setEntries(response.data.items);
      setTotal(response.data.total);
    } catch (err) {
      console.error('Error fetching journal entries:', err);
      toast.error('Failed to load journal entries');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const handleViewEntry = async (entryId) => {
    setDetailLoading(true);
    try {
      const response = await axios.get(`${API}/finance/hub/journal-entries/${entryId}`);
      setSelectedEntry(response.data);
    } catch (err) {
      toast.error('Failed to load journal entry details');
    } finally {
      setDetailLoading(false);
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-4" data-testid="journal-entries-list">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
            <Book className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Journal Entries</h2>
            <p className="text-sm text-muted-foreground">{total} entries found</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={fetchEntries} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search entries..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="pl-9"
                />
              </div>
            </div>
            <Select 
              value={filters.status || 'all'} 
              onValueChange={(v) => setFilters({ ...filters, status: v === 'all' ? '' : v })}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="posted">Posted</SelectItem>
                <SelectItem value="reversed">Reversed</SelectItem>
              </SelectContent>
            </Select>
            <Select 
              value={filters.source_type || 'all'} 
              onValueChange={(v) => setFilters({ ...filters, source_type: v === 'all' ? '' : v })}
            >
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="invoice">Invoice</SelectItem>
                <SelectItem value="payment">Payment</SelectItem>
                <SelectItem value="manual">Manual</SelectItem>
                <SelectItem value="reversal">Reversal</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <Input
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                className="w-[150px]"
                placeholder="Start"
              />
              <span className="text-muted-foreground">to</span>
              <Input
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                className="w-[150px]"
                placeholder="End"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : entries.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Book className="h-10 w-10 mx-auto mb-2 opacity-50" />
              <p>No journal entries found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Entry #</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Memo</TableHead>
                  <TableHead className="text-right">Debit</TableHead>
                  <TableHead className="text-right">Credit</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[80px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((entry) => {
                  const totalDebit = entry.lines?.reduce((sum, l) => sum + (l.debit || 0), 0) || 0;
                  const totalCredit = entry.lines?.reduce((sum, l) => sum + (l.credit || 0), 0) || 0;
                  
                  return (
                    <TableRow key={entry.id} className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800">
                      <TableCell className="font-mono text-sm">{entry.entry_number}</TableCell>
                      <TableCell>{entry.entry_date}</TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {SOURCE_LABELS[entry.source_type] || entry.source_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate text-sm text-muted-foreground">
                        {entry.memo || entry.description || '—'}
                      </TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(totalDebit)}</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(totalCredit)}</TableCell>
                      <TableCell>
                        <Badge className={STATUS_COLORS[entry.status]}>
                          {entry.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleViewEntry(entry.id)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {page * pageSize + 1} - {Math.min((page + 1) * pageSize, total)} of {total}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {page + 1} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Detail Dialog */}
      <JournalEntryDetail
        entry={selectedEntry}
        open={!!selectedEntry}
        onClose={() => setSelectedEntry(null)}
      />
    </div>
  );
}
